import axios from "@app/Security/AxiosAuthentication";
import PageInterface from "@app/Interface/PageInterface";
import PageModel from "@app/Model/PageModel";
import GeneralPageAcfInterface from "@app/Interface/GeneralPageAcfInterface";
import ApiEndpoints from "@app/utils/ApiEndpoints";
import DefaultNotificationThresholdsInterface from "@app/Interface/DefaultNotificationThresholdsInterface";
import ApiCache from "./ApiCache";
import AppSettings from "@app/utils/AppSettings";
import PageResponseInterface from "@app/Interface/PageResponseInterface";
import { AxiosRequestConfig } from "axios";
import htmlParser from "@app/utils/htmlParser";
import RollbarHelper from "@app/utils/RollbarHelper";

export default class PageHelper {

    /**
     * gets and parses wordpress page content by page id
     * 
     * @param pageId 
     * @returns 
     */
    static getPageData = async (pageId: number): Promise<PageInterface> => {

        const baseUrl: string = process.env.API_BASEURL ?? "";

        const endPoint: string = ApiEndpoints.PAGES + "/" + pageId;

        const url = baseUrl + endPoint;

        const cacheData = ApiCache.get(url);

        if (cacheData) {
            const cachedPageData: PageInterface = JSON.parse(cacheData.content);

            return cachedPageData;
        }

        return await axios.get(url, {
            withCredentials: true,
            headers: {
                "Content-Type": "application/json"
            }
        })
            .then(response => {

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const data: PageResponseInterface = response.data;

                const pageData = new PageModel();
                pageData.id = Number(data.id);
                pageData.title = data.title;
                pageData.slug = data.slug;
                pageData.template = data.template;
                pageData.subfields = data.subfields;
                pageData.acf = PageHelper.getAcfJson(data.acf);
                pageData.content = data.content;

                // save to cache
                ApiCache.set(url, pageData, AppSettings.getPageCacheStaleMinutes())

                return pageData;

            })
            .catch(error => {

                RollbarHelper.logRequestError(error, "getPageData()");

                // return an empty set of navItems
                const emptyItem: PageInterface = new PageModel();

                return emptyItem;

            });
    }

    /**
     * Gets all the pages fro a template. Used by notifications to create links.
     * 
     * @param controller AbortController - Handles if component is unmounted or cancel button is added
     * @param fpuId 
     * @param templateName 
     * @returns 
     */
    static getAllPagesByTemplateName = async (controller: AbortController, fpuId: number, templateName: string): Promise<PageInterface[]> => {

        const baseUrl: string = process.env.API_BASEURL ?? "";

        const endPoint: string = ApiEndpoints.PAGES + `?fpu=${fpuId}&template=${templateName}`;

        const url = baseUrl + endPoint;

        const options: AxiosRequestConfig<never> = {
            withCredentials: true,
            signal: controller.signal
        };

        try {

            const response = await axios.get(url, options);

            if (response.status === 200) {

                const pages: PageInterface[] = [];

                const dataItems: PageResponseInterface[] = response.data["hydra:member"];

                if (Array.isArray(dataItems)) {

                    dataItems.forEach(item => {

                        const pageData = new PageModel();
                        pageData.id = Number(item.id);
                        pageData.title = item.title;
                        pageData.slug = item.slug;
                        pageData.template = item.template;
                        pageData.subfields = item.subfields;
                        pageData.acf = PageHelper.getAcfJson(item.acf);
                        pageData.content = item.content;

                        pages.push(pageData);

                    });

                }

                return pages;

            }

        } catch (error) {

            RollbarHelper.logRequestError(error, "getAllPagesByTemplateName()");

            return [];

        }

        return [];

    }

    /**
     * Gets the first page that uses the template name.
     * 
     * @param controller AbortController - handles when component is unmounted
     * @param fpuId
     * @param templateName 
     * @returns 
     */
    static getPageDataByTemplateName = async (controller: AbortController, fpuId: number, templateName: string, reportType?: string): Promise<PageInterface> => {

        const baseUrl: string = process.env.API_BASEURL ?? "";

        const endPoint: string = ApiEndpoints.PAGES + `?fpu=${fpuId}&template=${templateName}`;

        const url = baseUrl + endPoint;

        const cacheData = ApiCache.get(url);

        if (cacheData) {
            const cachedPageData: PageInterface = JSON.parse(cacheData.content);

            return cachedPageData;
        }

        return await axios.get(url, {
            withCredentials: true,
            signal: controller.signal
        })
            .then(response => {

                const data: PageInterface[] = response.data["hydra:member"];

                // get the first match. 
                if (data.length > 0) {

                    let pageData: PageInterface;

                    if (reportType) {
                        pageData = PageHelper.getPageDataByReportType(data, reportType);
                    } else {
                        pageData = data[0];
                    }

                    let acfContent = "";

                    if (typeof pageData.title === "string") {
                        pageData.title = htmlParser.htmlDecode(pageData.title);
                    }

                    if (typeof pageData.acf === "string") {
                        acfContent = pageData.acf;
                    }

                    pageData.acf = PageHelper.getAcfJson(acfContent);

                    // save to cache
                    ApiCache.set(url, pageData, AppSettings.getPageCacheStaleMinutes())

                    return pageData;

                } else {

                    console.info(`page not found for template ${templateName}`);

                    // return an empty set of navItems
                    const emptyItem: PageInterface = new PageModel();

                    return emptyItem;
                }

            })
            .catch(error => {

                RollbarHelper.logRequestError(error, "getPageDataByTemplateName()");

                // return an empty set of navItems
                const emptyItem: PageInterface = new PageModel();

                return emptyItem;

            });
    }

    /**
     * Attempts to find the page based on report type 
     * TODO: getPageDataByReportType and getPageDataByChartType could get merged
     * @param pages 
     * @param reportType 
     * @returns 
     */
    static getPageDataByReportType = (pages: PageInterface[], reportType: string): PageInterface => {

        const pageData = pages.find((page) => {

            let acfContent = "";

            let acfData: GeneralPageAcfInterface = {};

            if (typeof page.acf === "string") {

                // need to convert string to object
                acfContent = page.acf;

                acfData = PageHelper.getAcfJson(acfContent);

            } else {

                // content was already converted
                acfData = page.acf;
            }

            return acfData.report_type === reportType;

        });

        if (pageData) {
            return pageData;
        } else {
            return new PageModel();
        }

    }

    /**
     * Attempts to find the page based on chart type 
     * 
     * @param pages
     * @param chartType 
     * @returns 
     */
    static getPageDataByChartType = (pages: PageInterface[], chartType: string): PageInterface => {

        const pageData = pages.find((page) => {

            let acfContent = "";

            let acfData: GeneralPageAcfInterface = {};

            if (typeof page.acf === "string") {

                // need to convert string to object
                acfContent = page.acf;

                acfData = PageHelper.getAcfJson(acfContent);

            } else {

                // content was already converted
                acfData = page.acf;
            }

            return acfData.chart_type === chartType;

        });

        if (pageData) {
            return pageData;
        } else {
            return new PageModel();
        }

    }

    /**
     * Gets the first page that matches the path
     * 
     * @param controller AbortController - handles if component unmounts
     * @param fpuId
     * @param path 
     * @returns 
     */
    static getPageDataByPath = async (controller: AbortController, fpuId: number, path: string): Promise<PageInterface> => {

        // querystings will return incorrect path by adding an ending slash, we need to remove it.
        if (path !== "/" && path.endsWith("/")) {
            path = path.slice(0, -1);
        }

        const baseUrl: string = process.env.API_BASEURL ?? "";

        const endPoint: string = ApiEndpoints.PAGES + `?fpu=${fpuId}&path=${path}`;

        const url = baseUrl + endPoint;

        const cacheData = ApiCache.get(url);

        if (cacheData) {
            const cachedPageData: PageInterface = JSON.parse(cacheData.content);

            return cachedPageData;
        }

        return await axios.get(url, {
            withCredentials: true,
            signal: controller.signal
        })
            .then(response => {

                const data: PageInterface[] = response.data["hydra:member"];

                // get the first match. 
                if (data.length > 0) {

                    const pageData: PageInterface = data[0];

                    let acfContent = "";

                    if (typeof pageData.title === "string") {
                        pageData.title = htmlParser.htmlDecode(pageData.title);
                    }

                    if (typeof pageData.acf === "string") {
                        acfContent = pageData.acf;
                    }

                    pageData.acf = PageHelper.getAcfJson(acfContent);

                    // save to cache
                    ApiCache.set(url, pageData, AppSettings.getPageCacheStaleMinutes())

                    return pageData;

                } else {

                    console.info(`page not found for path ${path}`);

                    // return an empty set of navItems
                    const emptyItem: PageInterface = new PageModel();

                    return emptyItem;
                }

            })
            .catch(error => {

                RollbarHelper.logRequestError(error, "getPageDataByPath()");

                // return an empty set of navItems
                const emptyItem: PageInterface = new PageModel();

                return emptyItem;

            });
    }

    /**
     * non-binding/don't wait for response function to get page data
     * and save it to the cache.
     * 
     * @param fpuId 
     * @param path 
     */
    static getPageDataByPathNonAsync = (fpuId: number, path: string): void => {

        const baseUrl: string = process.env.API_BASEURL ?? "";

        const endPoint: string = ApiEndpoints.PAGES + `?fpu=${fpuId}&path=${path}`;

        const url = baseUrl + endPoint;

        const options = {
            withCredentials: true
        };

        axios.get(url, options)
            .then(response => {

                const data: PageInterface[] = response.data["hydra:member"];

                // get the first match. 
                if (data.length > 0) {

                    const pageData: PageInterface = data[0];

                    let acfContent = "";

                    if (typeof pageData.title === "string") {
                        pageData.title = htmlParser.htmlDecode(pageData.title);
                    }

                    if (typeof pageData.acf === "string") {
                        acfContent = pageData.acf;
                    }

                    pageData.acf = PageHelper.getAcfJson(acfContent);

                    // save to cache
                    ApiCache.set(url, pageData, AppSettings.getPageCacheStaleMinutes())

                }

            })
            .catch(error => {

                RollbarHelper.logRequestError(error, "getPageDataByPathNonAsync()");

            });

    }

    /**
     * Converts the acf string data to a strongly typed object.
     * 
     * @param acf Advanced custom fields associated with the page
     * @returns 
     */
    static getAcfJson(acf: string): GeneralPageAcfInterface {

        let acfData: GeneralPageAcfInterface = {};

        // make sure we have data
        if (acf) {
            acfData = JSON.parse(acf);

            // make sure to convert min/max values from string to number.
            if (acfData.default_notification_thresholds) {

                acfData.default_notification_thresholds.forEach((threshold: DefaultNotificationThresholdsInterface, index: number) => {

                    // min
                    let min = Number(threshold.threshold_value_min);

                    if (isNaN(min)) {
                        min = 0;
                    }

                    threshold.threshold_value_min = min;

                    // max
                    let max = Number(threshold.threshold_value_max);

                    if (isNaN(max)) {
                        max = 0;
                    }

                    threshold.threshold_value_max = max;

                    // guardrail
                    let guardrailValue = Number(threshold.guardrail_value);

                    if (isNaN(guardrailValue)) {
                        guardrailValue = 0;
                    }

                    threshold.guardrail_value = guardrailValue;
                    
                    if (acfData.default_notification_thresholds) {
                        acfData.default_notification_thresholds[index] = threshold;
                    }

                });
            }
        }

        return acfData;
    }
}